import { FC, useState, useRef } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import getBuyerSupportLink from 'dibs-support-link/exports/getBuyerSupportLink';
import { trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2';
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker';

import { PdpSharedDutiesMessage_item$key } from './__generated__/PdpSharedDutiesMessage_item.graphql';
import { PdpSharedDutiesMessageQuery } from './__generated__/PdpSharedDutiesMessageQuery.graphql';
import { PdpSharedDutiesMessage_shipmentQuote$key } from './__generated__/PdpSharedDutiesMessage_shipmentQuote.graphql';

import InfoCircle from 'dibs-icons/exports/legacy/InfoCircle';
import InfoCircleFilled from 'dibs-icons/exports/legacy/InfoCircleFilled';
import CustomsMessaging from 'dibs-customs-messaging/exports/CustomsMessaging';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import { Link } from 'dibs-elements/exports/Link';

import styles from './PdpSharedDutiesMessage.scss';

type Props = {
    item: PdpSharedDutiesMessage_item$key | null;
    userId: string;
    shipmentQuote: PdpSharedDutiesMessage_shipmentQuote$key | null;
    currency: string;
};

const newDutiesMessage = {
    DDP: (
        <FormattedMessage
            id="shared.delivery.PDPSharedDutiesMessage.DDP"
            defaultMessage="Import duties and taxes are collected at checkout."
        />
    ),
    DAP: (
        <FormattedMessage
            id="shared.delivery.PDPSharedDutiesMessage.DAP"
            defaultMessage="Import duties may be collected upon arrival."
        />
    ),
};

const PdpSharedDutiesMessage: FC<Props> = props => {
    const { item: itemRef, userId, shipmentQuote: shipmentQuoteRef, currency } = props;
    const [dutiesTooltipVisible, setDutiesTooltipVisible] = useState(false);

    const trackerRef = useRef(null);

    const item = useFragment(
        graphql`
            fragment PdpSharedDutiesMessage_item on Item {
                serviceId
                isAuctionEnabled
            }
        `,
        itemRef
    );

    const shipmentQuote = useFragment(
        graphql`
            fragment PdpSharedDutiesMessage_shipmentQuote on ShipmentQuote {
                serviceId
            }
        `,
        shipmentQuoteRef
    );

    //Fetch incoterm for filtering customsMessage and dutiesTax
    const data = useLazyLoadQuery<PdpSharedDutiesMessageQuery>(
        graphql`
            query PdpSharedDutiesMessageQuery(
                $buyerId: String
                $itemId: String
                $destinationCountry: String
                $shipmentQuoteId: String
            ) {
                viewer {
                    newDutiesMsgAndTaxEnabled: featureFlag(feature: "newDutiesMessageAndTax")
                    customsDuties(
                        buyerId: $buyerId
                        itemId: $itemId
                        destinationCountry: $destinationCountry
                        shipmentQuoteId: $shipmentQuoteId
                    ) {
                        crossCustomsBorder
                        incoterm
                    }
                }
            }
        `,
        {
            buyerId: userId,
            itemId: item?.serviceId,
            destinationCountry: '',
            shipmentQuoteId: shipmentQuote?.serviceId || '',
        }
    );

    const viewer = data?.viewer;

    const newDutiesMsgAndTaxEnabled = viewer?.newDutiesMsgAndTaxEnabled;
    const customsDutiesInfo = viewer?.customsDuties;
    const transactionIsDDP = customsDutiesInfo?.incoterm === 'DDP';
    const transactionIsCrossCustomsBorder = customsDutiesInfo?.crossCustomsBorder;
    const { serviceId: itemId, isAuctionEnabled } = item || {};

    if (isAuctionEnabled) {
        //we want to show a generic warning for all auction items
        return (
            <div className={styles.dutiesMessage}>
                <FormattedMessage
                    id="pdp.dutiesMessage.auctions"
                    defaultMessage="Customs duties and taxes may apply to international purchases. {learnMore} in our support center."
                    values={{
                        learnMore: (
                            <Link
                                href={getBuyerSupportLink(
                                    '/en-us/articles/14491601612827-Customs-Duties-and-Taxes-for-International-Shipments'
                                )}
                                target="_blank"
                            >
                                <FormattedMessage
                                    id="pdp.dutiesMessage.auctionsLink"
                                    defaultMessage="Learn more"
                                />
                            </Link>
                        ),
                    }}
                />
            </div>
        );
    }

    let dutiesMessage = null;
    let dutiesTooltipMessage = null;
    if (newDutiesMsgAndTaxEnabled) {
        //Show new messaging if FF is on
        if (transactionIsCrossCustomsBorder) {
            dutiesMessage = transactionIsDDP ? newDutiesMessage.DDP : newDutiesMessage.DAP;
            dutiesTooltipMessage = transactionIsDDP ? (
                <FormattedMessage
                    id="pdp.dutiesMessage.DDP-tooltip"
                    defaultMessage="Applicable duties, taxes, and customs fees are included in your order total. You will not be required to pay additional fees upon arrival."
                />
            ) : (
                <FormattedMessage
                    id="pdp.dutiesMessage.DAP-tooltip"
                    defaultMessage="You may be required to to pay applicable duties, taxes, and customs to the shipping carrier upon receipt."
                />
            );
        }
    } else {
        //FF is off, show old customs Message
        dutiesMessage = <CustomsMessaging userId={userId} itemId={itemId} currency={currency} />;
    }

    const showDutiesTooltip =
        newDutiesMsgAndTaxEnabled && transactionIsCrossCustomsBorder ? (
            <span
                className={styles.wrapper}
                onClick={e => {
                    e.preventDefault();
                    setDutiesTooltipVisible(!dutiesTooltipVisible);
                }}
                onKeyDown={() => setDutiesTooltipVisible(!dutiesTooltipVisible)}
                role="button"
                tabIndex={0}
            >
                <span>
                    {!dutiesTooltipVisible && <InfoCircle className={styles.infoCircle} />}
                    {dutiesTooltipVisible && <InfoCircleFilled className={styles.infoCircle} />}
                    <Tooltip
                        isVisible={dutiesTooltipVisible}
                        align="left"
                        onClose={() => setDutiesTooltipVisible(false)}
                    >
                        {dutiesTooltipMessage}
                    </Tooltip>
                </span>
            </span>
        ) : null;

    return (
        <div className={styles.dutiesMessage} ref={trackerRef}>
            {dutiesMessage}
            {showDutiesTooltip}
            <VisibilityTracker
                elementRef={trackerRef}
                onVisibilityChange={({ isVisible }) =>
                    isVisible &&
                    newDutiesMsgAndTaxEnabled &&
                    transactionIsCrossCustomsBorder &&
                    trackAbTestV2Variant('DAP-Checkout-Treatment-Test-Q3-2023')
                }
            />
        </div>
    );
};

export default PdpSharedDutiesMessage;
